import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { Guid } from "../Utils/Guid";
import { List } from "../Utils/List";
import { Iterable } from "../Utils/ObjUtils";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { MonitorType } from "../Core/MonitorType";
import { HashSet } from "../Utils";


export interface MonitorSettingsFactory {
	CreateMonitorSettings(monitorType: MonitorType): MonitorSettingsBase;
}

export interface MonitorSettingsEntryFactory {
	CreateMonitorSettingsEntry(monitorType: MonitorType): MonitorSettingsEntry;
}


export abstract class MonitorSettingsEntry implements ObjectSerializer {

	public static Factory: MonitorSettingsEntryFactory;

	public abstract get DisplayName(): string;

	public Guid: Guid;
	public get HasGuid(): boolean {
		return (!!this.Guid && !this.Guid.Equals(Guid.Empty));
	}

	public PolicyGuid: Guid;
	public get HasPolicyGuid(): boolean {
		return (!!this.PolicyGuid && !this.PolicyGuid.Equals(Guid.Empty));
	}

	public DeviceGuid: Guid;
	public get HasDeviceGuid(): boolean {
		return (!!this.DeviceGuid && !this.DeviceGuid.Equals(Guid.Empty));
	}

	public GenerateGuid(): void {
		console.assert(!this.HasGuid);
		this.Guid = Guid.Create();
	}

	constructor() {
		this.Guid = Guid.Empty;
		this.PolicyGuid = Guid.Empty;
		this.DeviceGuid = Guid.Empty;
	}

	public static CreateInstance(monitorType: MonitorType): MonitorSettingsEntry {
		return MonitorSettingsEntry.Factory.CreateMonitorSettingsEntry(monitorType);
	}

	public CopyConfig(other: MonitorSettingsEntry): void {
		if (!other)
			return;

		this.Guid = other.Guid;
		this.PolicyGuid = other.PolicyGuid;
		this.DeviceGuid = other.DeviceGuid;
	}

	public Copy(other: MonitorSettingsEntry): void {
		if (!other || other == this)
			return;

		this.CopyConfig(other);
	}

	public Write(obj: any, context: any): void {

		// Called by subclass Write methods

		if (this.HasGuid)
			obj.guid = ValueEncoder.EncodeGuid(this.Guid);

		if (this.HasPolicyGuid)
			obj.policy_guid = ValueEncoder.EncodeGuid(this.PolicyGuid);

		if (this.HasDeviceGuid)
			obj.device_guid = ValueEncoder.EncodeGuid(this.DeviceGuid);
	}

	public Read(obj: any, context: any): void {

		// Called by subclass Read methods

		if (obj.guid !== undefined)
			this.Guid = ValueEncoder.DecodeGuid(obj.guid);

		if (obj.policy_guid !== undefined)
			this.PolicyGuid = ValueEncoder.DecodeGuid(obj.policy_guid);

		if (obj.device_guid !== undefined)
			this.DeviceGuid = ValueEncoder.DecodeGuid(obj.device_guid);
	}
}


export abstract class MonitorSettingsBase implements ObjectSerializer {

	private static readonly BASE_DEFAULT_LIMIT_COUNT = 10;

	public static Factory: MonitorSettingsFactory;

	public MonitorType: MonitorType;

	public MonitorPolicyId: Guid;
	public get HasMonitorPolicyId(): boolean {
		return (!!this.MonitorPolicyId && !this.MonitorPolicyId.Equals(Guid.Empty));
	}

	public MonitorProtocol: string;
	public get HasMonitorProtocol(): boolean {
		return !!this.MonitorProtocol;
	}

	public PollByHostname: boolean;

	public UseThresholds: boolean;

	public CriticalThreshold: number;
	public get HasCriticalThreshold(): boolean {
		return (this.CriticalThreshold >= 0);
	}

	public WarningThreshold: number;
	public get HasWarningThreshold(): boolean {
		return (this.WarningThreshold >= 0);
	}

	public LimitCount: number;
	public get HasLimitCount(): boolean {
		return (this.LimitCount > 0);
	}

	constructor() {
		this._Init();
	}

	private _Init(): void {
		this.MonitorType = MonitorType.None;
		this.MonitorPolicyId = Guid.Empty;
		this.MonitorProtocol = ""; // DEFAULT (which means use the DISCOVERED credentials)
		this.PollByHostname = false;

		this.UseThresholds = false;
		this.CriticalThreshold = -1;
		this.WarningThreshold = -1;
		this.LimitCount = MonitorSettings.BASE_DEFAULT_LIMIT_COUNT;
	}

	public static CreateInstance(monitorType: MonitorType): MonitorSettingsBase {
		return MonitorSettingsBase.Factory.CreateMonitorSettings(monitorType);
	}

	public ApplyParentGuids(policyGuid: Guid, deviceGuid: Guid): void {
		return;
	}

	public ApplyPolicyGuid(policyGuid: Guid): void {
		return;
	}

	public ApplyDeviceGuid(deviceGuid: Guid): void {
		return;
	}

	public KeepDeviceGuids(deviceGuid: Guid): void {
		return;
	}

	public SetEnabled(enabled: boolean): void {
		return;
	}

	public ClearEntries(): void {
		return;
	}

	public abstract GetEntries(): List<MonitorSettingsEntry>;

	public get HasGuids(): boolean {
		return true;
	}

	public VerifyGuids(): boolean {
		return false;
	}

	public VerifyGuids_MonitorSettings(policySettings: MonitorSettingsBase): boolean {
		return false;
	}

	private Private_Copy(other: MonitorSettingsBase): void {
		if (!other)
			return;

		this._Init();

		this.MonitorType = other.MonitorType;
		this.MonitorPolicyId = other.MonitorPolicyId;

		if (other.HasMonitorProtocol)
			this.MonitorProtocol = other.MonitorProtocol;

		this.PollByHostname = other.PollByHostname;

		this.UseThresholds = other.UseThresholds;

		if (other.HasCriticalThreshold)
			this.CriticalThreshold = other.CriticalThreshold;

		if (other.HasWarningThreshold)
			this.WarningThreshold = other.WarningThreshold;

		if (other.HasLimitCount)
			this.LimitCount = other.LimitCount;
	}

	public Copy(other: MonitorSettingsBase): void {
		this.Private_Copy(other);
	}

	public CopyConfig(other: MonitorSettingsBase): void {
		this.Private_Copy(other);
	}

	public CopySettings(other: MonitorSettingsBase): void {
		this.Private_Copy(other);
	}

	public get Count(): number {
		return 0;
	}

	public Clone(): MonitorSettingsBase {
		let newSettings: MonitorSettingsBase = MonitorSettingsBase.CreateInstance(this.MonitorType);
		newSettings.Copy(this);
		return newSettings;
	}

	public CloneConfig(): MonitorSettingsBase {
		let newSettings: MonitorSettingsBase = MonitorSettingsBase.CreateInstance(this.MonitorType);
		newSettings.CopyConfig(this);
		return newSettings;
	}

	public Write(obj: any, context: any): void {

		// Called by subclass Write methods

		obj.monitor_type = MonitorType[this.MonitorType];

		if (this.HasMonitorPolicyId)
			obj.monitor_policy_id = ValueEncoder.EncodeGuid(this.MonitorPolicyId);

		if (this.HasMonitorProtocol)
			obj.monitor_protocol = this.MonitorProtocol;

		obj.poll_by_hostname = ValueEncoder.EncodeBoolean(this.PollByHostname);
		obj.use_threshold_values = ValueEncoder.EncodeBoolean(this.UseThresholds);

		if (this.HasCriticalThreshold)
			obj.critical_threshold = ValueEncoder.EncodeInteger(this.CriticalThreshold);

		if (this.HasWarningThreshold)
			obj.warning_threshold = ValueEncoder.EncodeInteger(this.WarningThreshold);

		if (this.HasLimitCount)
			obj.limit_count = ValueEncoder.EncodeInteger(this.LimitCount);
	}

	public Read(obj: any, context: any): void {

		// Called by subclass Read methods

		if (obj.monitor_type !== undefined)
			this.MonitorType = MonitorType[<string>obj.monitor_type];

		if (obj.monitor_policy_id !== undefined)
			this.MonitorPolicyId = ValueEncoder.DecodeGuid(obj.monitor_policy_id);

		if (obj.monitor_protocol !== undefined)
			this.MonitorProtocol = obj.monitor_protocol;

		if (obj.poll_by_hostname !== undefined)
			this.PollByHostname = ValueEncoder.DecodeBoolean(obj.poll_by_hostname);

		if (obj.use_threshold_values !== undefined)
			this.UseThresholds = ValueEncoder.DecodeBoolean(obj.use_threshold_values);

		if (obj.critical_threshold !== undefined)
			this.CriticalThreshold = ValueEncoder.DecodeInteger(obj.critical_threshold);

		if (obj.warning_threshold !== undefined)
			this.WarningThreshold = ValueEncoder.DecodeInteger(obj.warning_threshold);

		if (obj.limit_count !== undefined)
			this.LimitCount = ValueEncoder.DecodeInteger(obj.limit_count);
	}
}


export abstract class MonitorSettings<K extends MonitorSettingsEntry>
	extends MonitorSettingsBase implements Iterable<K> {

	private _entryList: List<K>;

	constructor() {
		super();

		this._entryList = new List<K>();
	}

	protected abstract get ObjSerEntriesName(): string;

	protected abstract CreateEntry(): K;

	public abstract Find(other: K): K;

	public Find_Guid(entryGuid: Guid): K {

		let foundEntry: K = null;

		for (let i = 0; i < this._entryList.Count; ++i) {

			let entry: K = this._entryList.Get(i);

			if (!entry.Guid.Equals(entryGuid))
				continue;

			foundEntry = entry;
			break;
		}

		return foundEntry;
	}

	public Find_Guids(entryGuid: Guid, policyGuid: Guid, deviceGuid: Guid) {

		let foundEntry: K = null;

		for (let i = 0; i < this._entryList.Count; ++i) {

			let entry: K = this._entryList.Get(i);

			if (entryGuid) {
				if (!entry.Guid.Equals(entryGuid))
					continue;
			}

			if (policyGuid) {
				if (!entry.PolicyGuid.Equals(policyGuid))
					continue;
			}

			if (deviceGuid) {
				if (!entry.DeviceGuid.Equals(deviceGuid))
					continue;
			}

			foundEntry = entry;
			break;
		}

		return foundEntry;
	}

	public get Count(): number {
		return this._entryList.Count;
	}

	public GetEntries(): List<MonitorSettingsEntry> {
		return this._entryList;
	}

	public Get(index: number): K {
		return this._entryList.Get(index);
	}

	public Clear(): void {
		this._entryList.Clear();
	}

	public ForEach(callbackfn: (value: K, index: number, array: K[]) => void, thisArg?: any): void {
		this._entryList.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: K, index: number, array: K[]) => boolean, thisArg?: any): boolean {
		return this._entryList.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: K, index: number, array: K[]) => boolean, thisArg?: any): boolean {
		return this._entryList.Every(callbackfn, thisArg);
	}

	public Copy(other: MonitorSettingsBase): void {

		if (!other || other == this)
			return;

		let otherSettings = other as MonitorSettings<K>;
		if (!otherSettings)
			return;

		this.Clear();

		super.Copy(other);

		this.CopySettings(otherSettings);
		this.CopyEntries(otherSettings);
	}

	public CopyConfig(other: MonitorSettingsBase): void {

		if (other == null || other == this)
			return;

		let otherSettings = other as MonitorSettings<K>;
		if (!otherSettings)
			return;

		super.CopyConfig(other);

		this.CopySettings(otherSettings);
		this.CopyEntryConfigs(otherSettings);
	}

	private CopyEntryConfigs(otherSettings: MonitorSettings<K>): void {

		otherSettings._entryList.ForEach((entry: K) => {
			this.AddCopyConfig(entry);
		}, this);


		// REMOVE any
		let removeList = new List<K>();
		this._entryList.ForEach((entry: K) => {

			let otherEntry: K = otherSettings.Find(entry);

			if (!otherEntry)
				removeList.Add(entry);
		}, this);

		removeList.ForEach((entry: K) => {
			this.Remove(entry);
		}, this);
	}

	private CopyEntries(otherSettings: MonitorSettings<K>): void {

		if (!otherSettings)
			return;

		otherSettings._entryList.ForEach((entry: K) => {
			let localEntry: K = this.AddCopy(entry);
			if (localEntry) {
				localEntry.Copy(entry);
			}
		}, this);
	}

	public Contains(other: K): boolean {
		return !!this.Find(other);
	}

	public Add(other: K): K {
		let entry: K = this.Find(other);

		if (!entry) {
			this._entryList.Add(other);
			return other;
		}
		else {
			return entry;
		}
	}

	public AddCopy(other: K): K {
		let entry: K = this.Find(other);

		if (!entry) {
			entry = this.CreateEntry();
			this._entryList.Add(entry);
		}

		if (entry != null)
			entry.Copy(other);

		return entry;
	}

	public AddCopyConfig(other: K): K {
		let entry: K = this.Find(other);

		if (!entry) {
			entry = this.CreateEntry();
			this._entryList.Add(entry);
		}

		if (entry)
			entry.CopyConfig(other);

		return entry;
	}

	public ApplyParentGuids(policyGuid: Guid, deviceGuid: Guid): void {
		if (!policyGuid || policyGuid.Equals(Guid.Empty)) {
			return;
		}
		if (!deviceGuid || deviceGuid.Equals(Guid.Empty)) {
			return;
		}

		// ASSIGN parent POLICY GUID
		this.MonitorPolicyId = policyGuid;

		// PUSH POLICY/DEVICE GUIDs down to each entry
		this._entryList.ForEach((entry: K) => {
			entry.PolicyGuid = policyGuid;
			entry.DeviceGuid = deviceGuid;
		});
	}

	public ApplyPolicyGuid(policyGuid: Guid): void {
		if (!policyGuid || policyGuid.Equals(Guid.Empty)) {
			return;
		}

		this.MonitorPolicyId = policyGuid;

		// PUSH POLICY GUID down to each entry
		this._entryList.ForEach((entry: K) => {
			entry.PolicyGuid = policyGuid;
		});
	}

	public ApplyDeviceGuid(deviceGuid: Guid): void {
		if (!deviceGuid || deviceGuid.Equals(Guid.Empty)) {
			return;
		}

		this._entryList.ForEach((entry: K) => {
			if (!entry.HasDeviceGuid) {
				entry.DeviceGuid = deviceGuid;
			}
		});
	}

	public KeepDeviceGuids(deviceGuid: Guid): void {
		if (!deviceGuid || deviceGuid.Equals(Guid.Empty)) {
			return;
		}

		let removeList = new List<K>();

		this._entryList.ForEach((entry: K) => {
			if (!entry.DeviceGuid.Equals(deviceGuid)) {
				removeList.Add(entry);
			}
		});

		removeList.ForEach((entry: K) => {
			this.Remove(entry);
		});
	}

	public ClearEntries(): void {
		this.Clear();
	}

	public get HasGuids(): boolean {
		if (this.Count <= 0)
			return true;

		for (let i = 0; i < this._entryList.Count; ++i) {
			let srcEntry = this._entryList.Get(i);
			if (!srcEntry.HasGuid)
				return false;
		}

		return true;
	}

	public VerifyGuids(): boolean {

		let guidsAdded: boolean = false;

		if (this.Count <= 0)
			return guidsAdded;

		for (let i = 0; i < this._entryList.Count; ++i) {

			let srcEntry = this._entryList.Get(i);

			if (srcEntry.HasGuid)
				continue;

			srcEntry.GenerateGuid();
			guidsAdded = true;
		}

		return guidsAdded;
	}

	public get DeviceGuids(): HashSet<Guid> {

		let deviceGuids = new HashSet<Guid>();

		this._entryList.ForEach((entry: K) => {
			if (entry.HasDeviceGuid) {
				deviceGuids.Add(entry.DeviceGuid);
			}
		});

		return deviceGuids;
	}

	public Remove(entry: K): boolean {
		let oldEntry: K = this.Find(entry);

		if (oldEntry)
			return this._entryList.Remove(oldEntry);

		return false;
	}

	public Remove_Guids(entryGuid: Guid, policyGuid: Guid, deviceGuid: Guid): boolean {
		let oldEntry: K = this.Find_Guids(entryGuid, policyGuid, deviceGuid);

		if (oldEntry)
			return this._entryList.Remove(oldEntry);

		return false;
	}

	public Write(obj: any, context: any): void {

		// Called by subclass Write methods

		super.Write(obj, context);

		if (this.Count > 0) {
			obj[this.ObjSerEntriesName] = [];
			ObjSerUtils.WriteObjSerCollection(obj[this.ObjSerEntriesName], this._entryList, context);
		}
	}

	public Read(obj: any, context: any): void {

		// Called by subclass Read methods

		super.Read(obj, context);

		if (obj[this.ObjSerEntriesName] !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj[this.ObjSerEntriesName], this._entryList,
				(value: any) => { return this.CreateEntry(); }, context);
		}
	}
}


export class MonitorSettingsEntryKey {

	public Guid: Guid;
	public get HasGuid(): boolean {
		return (!!this.Guid && !this.Guid.Equals(Guid.Empty));
	}

	public PolicyGuid: Guid;
	public get HasPolicyGuid(): boolean {
		return (!!this.PolicyGuid && !this.PolicyGuid.Equals(Guid.Empty));
	}

	public DeviceGuid: Guid;
	public get HasDeviceGuid(): boolean {
		return (!!this.DeviceGuid && !this.DeviceGuid.Equals(Guid.Empty));
	}

	public get IsEmpty(): boolean {
		if (!!this.Guid && !this.Guid.Equals(Guid.Empty)) {
			return false;
		}
		if (!!this.PolicyGuid && !this.PolicyGuid.Equals(Guid.Empty)) {
			return false;
		}
		if (!!this.DeviceGuid && !this.DeviceGuid.Equals(Guid.Empty)) {
			return false;
		}
		return true;
	}

	constructor(guidOrEntry: Guid | MonitorSettingsEntry, policyGuid?: Guid, deviceGuid?: Guid) {

		this._Init();

		if (!guidOrEntry || !!(guidOrEntry instanceof Guid)) {
			let guid = <Guid>guidOrEntry;

			if (guid) {
				this.Guid = guid;
			}
			if (policyGuid) {
				this.PolicyGuid = policyGuid;
			}
			if (deviceGuid) {
				this.DeviceGuid = deviceGuid;
			}
		}
		else {
			let entry = <MonitorSettingsEntry>guidOrEntry;

			if (entry.HasGuid) {
				this.Guid = entry.Guid;
			}
			if (entry.HasPolicyGuid) {
				this.PolicyGuid = entry.PolicyGuid;
			}
			if (entry.HasDeviceGuid) {
				this.DeviceGuid = entry.DeviceGuid;
			}
		}
	}

	private _Init(): void {
		this.Guid = Guid.Empty;
		this.PolicyGuid = Guid.Empty;
		this.DeviceGuid = Guid.Empty;
	}

	public GetHashCode(): number {
		let hashCode = this.HashGuid(this.Guid) ^ this.HashGuid(this.PolicyGuid) ^ this.HashGuid(this.DeviceGuid);
		return hashCode;
	}

	private HashGuid(guid: Guid): number {
		return (guid ? guid.GetHashCode() : 0);
	}

	public Equals(obj: any): boolean {
		if (!obj)
			return false;

		let otherKey = obj as MonitorSettingsEntryKey;
		if (!otherKey)
			return false;

		if (!this.Guid.Equals(otherKey.Guid))
			return false;

		if (!this.PolicyGuid.Equals(otherKey.PolicyGuid))
			return false;

		if (!this.DeviceGuid.Equals(otherKey.DeviceGuid))
			return false;

		return true;
	}
}
